import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { X, Loader, DollarSign, Ban, FileDown, AlertTriangle } from 'lucide-react';
import { CUENTA_COBRO_DETALLE, ANULAR_PAGO, CANCELAR_CUENTA } from '../../graphql/cuentasCobro';
import { generarCuentaCobroPDF } from '../../utils/generarCuentaCobroPDF';
import ModalRegistrarPago from './ModalRegistrarPago';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function formatFecha(f) {
  if (!f) return '-';
  return new Date(f).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatFechaHora(f) {
  if (!f) return '-';
  return new Date(f).toLocaleString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* Mini-modal de confirmación con input opcional */
function ModalConfirmacion({ titulo, mensaje, conMotivo, onConfirm, onCancel, loading: cargando }) {
  const [motivo, setMotivo] = useState('');
  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={onCancel}>
      <div className="modal-cc modal-confirm-inner" onClick={e => e.stopPropagation()}>
        <div className="modal-confirm-icon"><AlertTriangle size={32} color="#f59e0b" /></div>
        <h3 className="modal-confirm-title">{titulo}</h3>
        <p className="modal-confirm-msg">{mensaje}</p>
        {conMotivo && (
          <textarea
            className="modal-confirm-textarea"
            placeholder="Motivo (obligatorio)..."
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={2}
          />
        )}
        <div className="modal-confirm-actions">
          <button className="btn-modal" onClick={onCancel} disabled={cargando}>Cancelar</button>
          <button
            className="btn-modal btn-modal--danger"
            onClick={() => onConfirm(motivo)}
            disabled={cargando || (conMotivo && !motivo.trim())}
          >
            {cargando ? 'Procesando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Mini-modal para mensajes de error */
function ModalError({ mensaje, onClose }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="modal-cc modal-confirm-inner" onClick={e => e.stopPropagation()}>
        <div className="modal-confirm-icon"><X size={32} color="#ef4444" /></div>
        <h3 className="modal-confirm-title">Error</h3>
        <p className="modal-confirm-msg">{mensaje}</p>
        <div className="modal-confirm-actions">
          <button className="btn-modal btn-modal--primary" onClick={onClose}>Aceptar</button>
        </div>
      </div>
    </div>
  );
}

function ModalCuentaDetalle({ cuentaId, onClose, onPagoRegistrado }) {
  const [modalPago, setModalPago] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { tipo, pagoId }
  const [errorMsg, setErrorMsg] = useState(null);

  const { data, loading, refetch } = useQuery(CUENTA_COBRO_DETALLE, {
    variables: { id: cuentaId },
    fetchPolicy: 'network-only',
  });

  const onMutationError = (err) => {
    setConfirmAction(null);
    setErrorMsg(err.message);
  };

  const [anularPago, { loading: anulando }] = useMutation(ANULAR_PAGO, {
    errorPolicy: 'none',
    onError: onMutationError,
  });
  const [cancelarCuenta, { loading: cancelando }] = useMutation(CANCELAR_CUENTA, {
    errorPolicy: 'none',
    onError: onMutationError,
  });

  const cuenta = data?.cuentaCobro;

  const handleAnularPago = (pagoId) => {
    setConfirmAction({ tipo: 'anular', pagoId });
  };

  const handleCancelar = () => {
    setConfirmAction({ tipo: 'cancelar' });
  };

  const ejecutarConfirmacion = async (motivo) => {
    try {
      if (confirmAction.tipo === 'anular') {
        await anularPago({ variables: { pago_id: confirmAction.pagoId, motivo } });
      } else {
        await cancelarCuenta({ variables: { id: parseInt(cuentaId), motivo } });
      }
      setConfirmAction(null);
      refetch();
      if (onPagoRegistrado) onPagoRegistrado();
    } catch (err) {
      // Handled by onError callback
    }
  };

  const handlePDF = () => {
    if (cuenta) generarCuentaCobroPDF(cuenta);
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-cc modal-cc--lg" onClick={e => e.stopPropagation()}>
        <div className="modal-cc__header">
          <h2>Detalle de Cuenta de Cobro</h2>
          <button className="btn-cerrar" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-cc__body">
          {loading || !cuenta ? (
            <div className="loading-overlay">
              <Loader size={32} className="spinner" />
            </div>
          ) : (
            <>
              {/* Info principal */}
              <div className="detalle-header">
                <div className="detalle-codigo">
                  <span className="label">Código</span>
                  <strong>{cuenta.codigo}</strong>
                </div>
                <span className={`badge badge--${cuenta.estado}`}>
                  {cuenta.estado.charAt(0).toUpperCase() + cuenta.estado.slice(1)}
                </span>
              </div>

              {/* Datos soldado */}
              <div className="detalle-seccion">
                <h3>Soldado</h3>
                <div className="detalle-grid">
                  <div><span>Nombre:</span> <strong>{cuenta.nombre_soldado}</strong></div>
                  <div><span>Documento:</span> {cuenta.tipo_documento} {cuenta.numero_documento}</div>
                  <div><span>Grado:</span> {cuenta.grado_militar_nombre || '-'}</div>
                  <div><span>Ubicación:</span> Hab {cuenta.habitacion_numero} / Cama {cuenta.cama_numero || '-'}</div>
                </div>
              </div>

              {/* Periodo */}
              <div className="detalle-seccion">
                <h3>Periodo</h3>
                <div className="detalle-grid">
                  <div><span>Mes:</span> <strong>{MESES[cuenta.periodo_mes - 1]} {cuenta.periodo_anio}</strong></div>
                  <div><span>Días:</span> {cuenta.dias_periodo}</div>
                  <div><span>Desde:</span> {formatFecha(cuenta.fecha_inicio_periodo)}</div>
                  <div><span>Hasta:</span> {formatFecha(cuenta.fecha_fin_periodo)}</div>
                </div>
              </div>

              {/* Montos */}
              <div className="detalle-montos">
                <div className="monto-item">
                  <span>Precio/noche</span>
                  <strong>{formatMoneda(cuenta.precio_noche)}</strong>
                </div>
                <div className="monto-item">
                  <span>Total ({cuenta.dias_periodo} noches)</span>
                  <strong>{formatMoneda(cuenta.monto_total)}</strong>
                </div>
                <div className="monto-item monto-item--verde">
                  <span>Pagado</span>
                  <strong>{formatMoneda(cuenta.monto_pagado)}</strong>
                </div>
                <div className="monto-item monto-item--rojo">
                  <span>Saldo Pendiente</span>
                  <strong>{formatMoneda(cuenta.saldo_pendiente)}</strong>
                </div>
              </div>

              {/* Historial de pagos */}
              <div className="detalle-seccion">
                <h3>Historial de Pagos</h3>
                {(!cuenta.pagos || cuenta.pagos.length === 0) ? (
                  <p className="sin-pagos">No hay pagos registrados</p>
                ) : (
                  <table className="tabla-pagos">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Fecha</th>
                        <th>Monto</th>
                        <th>Método</th>
                        <th>Referencia</th>
                        <th>Registrado por</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuenta.pagos.map(p => (
                        <tr key={p.id} className={p.anulado ? 'pago-anulado' : ''}>
                          <td className="td-codigo">{p.codigo}</td>
                          <td>{formatFechaHora(p.fecha_pago)}</td>
                          <td className="text-right">{formatMoneda(p.monto)}</td>
                          <td>{p.metodo_pago}</td>
                          <td>{p.referencia || '-'}</td>
                          <td>{p.registrado_por_nombre || '-'}</td>
                          <td>
                            {p.anulado ? (
                              <span className="anulado-tag" title={p.motivo_anulacion}>Anulado</span>
                            ) : (
                              <button
                                className="btn-anular-pago"
                                onClick={() => handleAnularPago(p.id)}
                                disabled={anulando}
                                title="Anular pago"
                              >
                                <Ban size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {cuenta.notas && (
                <div className="detalle-seccion">
                  <h3>Notas</h3>
                  <p className="detalle-notas">{cuenta.notas}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-cc__footer">
          {cuenta && cuenta.estado !== 'cancelada' && cuenta.estado !== 'pagada' && (
            <>
              <button className="btn-modal btn-modal--danger" onClick={handleCancelar} disabled={cancelando}>
                Cancelar Cuenta
              </button>
              <button className="btn-modal btn-modal--primary" onClick={() => setModalPago(true)}>
                <DollarSign size={16} /> Registrar Pago
              </button>
            </>
          )}
          {cuenta && (
            <button className="btn-modal" onClick={handlePDF}>
              <FileDown size={16} /> PDF
            </button>
          )}
          <button className="btn-modal" onClick={onClose}>Cerrar</button>
        </div>

        {modalPago && cuenta && (
          <ModalRegistrarPago
            cuenta={cuenta}
            onClose={() => setModalPago(false)}
            onSuccess={() => { setModalPago(false); refetch(); if (onPagoRegistrado) onPagoRegistrado(); }}
          />
        )}
      </div>
    </div>

    {confirmAction && (
      <ModalConfirmacion
        titulo={confirmAction.tipo === 'anular' ? 'Anular Pago' : 'Cancelar Cuenta'}
        mensaje={confirmAction.tipo === 'anular'
          ? '¿Está seguro de anular este pago? Esta acción no se puede deshacer.'
          : '¿Está seguro de cancelar esta cuenta de cobro?'}
        conMotivo
        onConfirm={ejecutarConfirmacion}
        onCancel={() => setConfirmAction(null)}
        loading={anulando || cancelando}
      />
    )}

    {errorMsg && (
      <ModalError mensaje={errorMsg} onClose={() => setErrorMsg(null)} />
    )}
    </>
  );
}

/* Estilos del detalle */
const style = document.createElement('style');
style.textContent = `
.detalle-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem; }
.detalle-codigo .label { display: block; font-size: 0.7rem; color: var(--hotel-text-secondary); text-transform: uppercase; }
.detalle-codigo strong { font-size: 1.1rem; font-family: monospace; }
.detalle-seccion { margin-bottom: 1.25rem; }
.detalle-seccion h3 { font-size: 0.85rem; font-weight: 700; color: var(--hotel-text-secondary); text-transform: uppercase; margin: 0 0 0.5rem 0; padding-bottom: 0.25rem; border-bottom: 1px solid var(--hotel-border); }
.detalle-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem 1rem; font-size: 0.875rem; }
.detalle-grid span { color: var(--hotel-text-secondary); }
.detalle-montos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem; margin-bottom: 1.25rem; }
.monto-item { display: flex; flex-direction: column; padding: 0.75rem; background: var(--hotel-background); border-radius: 8px; text-align: center; }
.monto-item span { font-size: 0.7rem; color: var(--hotel-text-secondary); text-transform: uppercase; margin-bottom: 0.25rem; }
.monto-item strong { font-size: 1.1rem; }
.monto-item--verde strong { color: #22c55e; }
.monto-item--rojo strong { color: #ef4444; }
.tabla-pagos { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.tabla-pagos th { padding: 0.5rem; text-align: left; font-size: 0.7rem; text-transform: uppercase; color: var(--hotel-text-secondary); border-bottom: 1px solid var(--hotel-border); }
.tabla-pagos td { padding: 0.5rem; border-bottom: 1px solid var(--hotel-border); }
.pago-anulado { opacity: 0.5; text-decoration: line-through; }
.anulado-tag { font-size: 0.7rem; color: #ef4444; font-weight: 600; }
.btn-anular-pago { background: none; border: none; color: #ef4444; cursor: pointer; padding: 0.2rem; display: flex; }
.btn-anular-pago:hover { opacity: 0.7; }
.sin-pagos { font-size: 0.85rem; color: var(--hotel-text-secondary); font-style: italic; }
.detalle-notas { font-size: 0.85rem; color: var(--hotel-text-secondary); white-space: pre-wrap; margin: 0; }
@media (max-width: 640px) { .detalle-montos { grid-template-columns: 1fr 1fr; } .detalle-grid { grid-template-columns: 1fr; } }
.modal-confirm-inner { max-width: 400px; text-align: center; padding: 1.5rem; }
.modal-confirm-icon { margin-bottom: 0.75rem; }
.modal-confirm-title { margin: 0 0 0.5rem; font-size: 1.1rem; }
.modal-confirm-msg { margin: 0 0 1rem; font-size: 0.875rem; color: var(--hotel-text-secondary); }
.modal-confirm-textarea { width: 100%; padding: 0.5rem; border: 1px solid var(--hotel-border); border-radius: 6px; resize: none; font-size: 0.85rem; margin-bottom: 1rem; background: var(--hotel-background); color: var(--hotel-text-primary); font-family: inherit; }
.modal-confirm-textarea:focus { outline: none; border-color: var(--hotel-primary); }
.modal-confirm-actions { display: flex; gap: 0.75rem; justify-content: center; }
`;
if (!document.getElementById('modal-detalle-styles')) {
  style.id = 'modal-detalle-styles';
  document.head.appendChild(style);
}

export default ModalCuentaDetalle;
